import { 
  collection, 
  doc, 
  updateDoc,
  deleteDoc,
  query, 
  where, 
  onSnapshot,
  Firestore,
} from 'firebase/firestore';
import { getFirestoreDB } from '../Config/firebaseConfig';
import { IFirestoreAlert, IFirestoreMessage } from '../Types/firestoreTypes';

// Get the initialized Firestore instance
const getFirestore = (): Firestore => {
  const db = getFirestoreDB();
  if (!db) {
    throw new Error('Firestore is not available. Firebase may not have loaded correctly.');
  }
  return db;
};


export class FirestoreService {
  // ===== ALERT-SPECIFIC METHODS =====
  // ***********************************

  /**
   * Start listening to all alerts for a specific user (centralized listener)
   */
  static startListeningToAllAlerts(
    userId: string, 
    onAlertsUpdate: (alerts: Array<{ id: string; data: IFirestoreAlert }>) => void,
    onError?: (error: Error) => void
  ): () => void {
    const firestore = getFirestore();
    const alertsRef = collection(firestore, 'users', userId, 'alerts');
    const q = query(
      alertsRef
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const alerts: Array<{ id: string; data: IFirestoreAlert }> = [];
        snapshot.forEach((doc) => {
          alerts.push({
            id: doc.id,
            data: doc.data() as IFirestoreAlert
          });
        });
        onAlertsUpdate(alerts);
      },
      (error) => {
        console.error('Error listening to all alerts:', error);
        if (onError) {
          onError(error);
        }
      }
    );

    return unsubscribe;
  }

  static async acknowledgeAlert(userId: string, alertId: string): Promise<void> {
    const firestore = getFirestore();

    try {
      const alertRef = doc(firestore, 'users', userId, 'alerts', alertId);
      
      await updateDoc(alertRef, {
        visibility: 'ACKNOWLEDGED'
      });
      
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      console.error('Error details:', {
        code: (error as any).code,
        message: (error as any).message,
        userId,
        alertId
      });
      throw error;
    }
  }

  static async acknowledgeMultipleAlerts(userId: string, alertIds: string[]): Promise<void> {
    const promises = alertIds.map(alertId => this.acknowledgeAlert(userId, alertId));
    await Promise.all(promises);
  }

  static async deleteAlert(userId: string, alertId: string): Promise<void> {
    const firestore = getFirestore();

    try {
      const alertRef = doc(firestore, 'users', userId, 'alerts', alertId);
      await deleteDoc(alertRef);
    } catch (error) {
      console.error('Error deleting alert:', error);
      console.error('Error details:', {
        code: (error as any).code,
        message: (error as any).message,
        userId,
        alertId
      });
      throw error;
    }
  }

  static async deleteMultipleAlerts(userId: string, alertIds: string[]): Promise<void> {
    const promises = alertIds.map(alertId => this.deleteAlert(userId, alertId));
    await Promise.all(promises);
  }

  static async markAlertAsNew(userId: string, alertId: string): Promise<void> {
    const firestore = getFirestore();

    try {
      const alertRef = doc(firestore, 'users', userId, 'alerts', alertId);
      await updateDoc(alertRef, {
        visibility: 'NEW'
      });
    } catch (error) {
      console.error('Error marking alert as new:', error);
      console.error('Error details:', {
        code: (error as any).code,
        message: (error as any).message,
        userId,
        alertId
      });
      throw error;
    }
  }
  // ===== END ALERT-SPECIFIC METHODS =====

  // ===== CLIENT MESSAGE-SPECIFIC METHODS =====
  // ***********************************

  /**
   * Start listening to client messages for a specific client
   */
  static startListeningToClientMessages(
    clientId: string, 
    onMessagesUpdate: (messages: Array<{ id: string; data: IFirestoreMessage }>) => void,
    onError?: (error: Error) => void
  ): () => void {
    const firestore = getFirestore();
    const messagesRef = collection(firestore, 'clients', clientId, 'messages');
    const q = query(
      messagesRef,
      where('enabled', '==', true)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const messages: Array<{ id: string; data: IFirestoreMessage }> = [];
        snapshot.forEach((doc) => {
          const data = doc.data() as IFirestoreMessage;
          messages.push({
            id: data.id, // Use the id field from the message data, not the Firestore doc ID
            data: data
          });
        });
        onMessagesUpdate(messages);
      },
      (error) => {
        console.error('Error listening to client messages:', error);
        if (onError) {
          onError(error);
        }
      }
    );

    return unsubscribe;
  }
  // ===== END CLIENT MESSAGE-SPECIFIC METHODS =====

  // ===== GENERAL MESSAGE-SPECIFIC METHODS =====
  // ***********************************

  /**
   * Start listening to general messages for all users
   */
  static startListeningToGeneralMessages(
    onMessagesUpdate: (messages: Array<{ id: string; data: IFirestoreMessage }>) => void,
    onError?: (error: Error) => void
  ): () => void {
    const firestore = getFirestore();
    const messagesRef = collection(firestore, 'general', 'messaging', 'messages');
    const q = query(
      messagesRef,
      where('enabled', '==', true)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const messages: Array<{ id: string; data: IFirestoreMessage }> = [];
        snapshot.forEach((doc) => {
          const data = doc.data() as IFirestoreMessage;
          messages.push({
            id: data.id, // Use the id field from the message data, not the Firestore doc ID
            data: data
          });
        });
        onMessagesUpdate(messages);
      },
      (error) => {
        console.error('Error listening to general messages:', error);
        if (onError) {
          onError(error);
        }
      }
    );

    return unsubscribe;
  }
  // ===== END GENERAL MESSAGE-SPECIFIC METHODS =====
} 